import { useState } from "react";

import {
  faEye,
  faFile,
  faPen,
  faTrashCan,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { Button } from "src/design-system/Button";
import { Icon, IconButton } from "src/design-system/Icon";
import { FileDownloadButton, FileViewer } from "src/files/FileViewer";
import { FileUploadController } from "src/manualReview/FileUploadController";
import {
  FILE_EXTENSIONS,
  FILE_TYPES,
  getFileNameParts,
  humanReadableSize,
} from "src/utils/files";

type CommonProps = {
  onFileSelect: (file: File, key: string) => void;
  onFileRemove: (key: string) => void;
  onFileNameChange: (name: string, key: string) => void;
  isUploading: boolean;
  isError: boolean;
  fieldKey: string;
  disabled?: boolean;
};

type SingleFileProps = CommonProps & {
  multiple?: false;
  data?: File | undefined;
};

type MultiFileProps = CommonProps & {
  multiple: true;
  data?: Map<string, File> | undefined;
};

type Props = SingleFileProps | MultiFileProps;

const getDropzoneIcon = (data?: File) => {
  if (!data) {
    return (
      <div className="mr-2 flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100">
        <Icon icon={faUpload} size="md" variant="active" />
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-lg">
      <Icon icon={faFile} size="md" variant="active" />
    </div>
  );
};

const FileTitle: React.FC<{
  data: File;
  onFileNameChange: (name: string, key: string) => void;
  fileKey: string;
}> = ({ data, onFileNameChange, fileKey }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { nameWithoutExt, extension } = getFileNameParts(data.name);

  const handleSave = (value: string) => {
    setIsEditing(false);
    onFileNameChange(`${value}${extension}`, fileKey);
  };

  if (isEditing) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <input
          className="pr-1 font-inter-semibold-13px text-gray-800"
          defaultValue={nameWithoutExt}
          autoFocus
          onBlur={(e) => handleSave(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave((e.target as HTMLInputElement).value);
            } else if (e.key === "Escape") {
              setIsEditing(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span
        className="cursor-text truncate font-inter-semibold-13px text-gray-800 hover:bg-gray-100"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      >
        {nameWithoutExt}
        <span className="text-gray-800">{extension}</span>
      </span>
      <IconButton
        icon={faPen}
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      />
    </div>
  );
};

const getDropzoneDescription = (data?: File) => {
  if (!data) {
    return "Supported file types: " + Object.values(FILE_TYPES).join(", ");
  }
  return `${Object.values(FILE_EXTENSIONS)
    .find((ext) => data.name.toLowerCase().endsWith(`.${ext}`))
    ?.toUpperCase()} · ${humanReadableSize(data.size)}`;
};

const FileListItem: React.FC<{
  fileKey: string;
  file: File;
  onFileNameChange: (name: string, key: string) => void;
  onFileRemove: (key: string) => void;
}> = ({ fileKey, file, onFileNameChange, onFileRemove }) => (
  <div className="w-full rounded-md border">
    <div className="flex justify-between gap-2 p-2">
      <div className="flex min-w-0 gap-1">
        {getDropzoneIcon(file)}
        <div className="flex min-w-0 flex-col">
          <span className="font-inter-semibold-13px text-gray-800">
            <FileTitle
              data={file}
              fileKey={fileKey}
              onFileNameChange={onFileNameChange}
            />
          </span>
          <span className="font-inter-normal-12px text-gray-500">
            {getDropzoneDescription(file)}
          </span>
          <span className="font-inter-normal-12px text-gray-500"></span>
        </div>
      </div>
      <div className="flex shrink-0 items-start gap-1">
        <FileViewer file={file}>
          <IconButton icon={faEye} size="xs" />
        </FileViewer>
        <FileDownloadButton file={file} />
        <IconButton
          icon={faTrashCan}
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            onFileRemove(fileKey);
          }}
        />
      </div>
    </div>
  </div>
);

export const FileDropzone: React.FC<Props> = (props) => {
  const {
    onFileSelect,
    onFileRemove,
    onFileNameChange,
    disabled,
    isError,
    isUploading,
    multiple,
    fieldKey,
  } = props;

  const fileEntries: [string, File][] = multiple
    ? Array.from((props.data ?? new Map()).entries())
    : props.data
      ? [[fieldKey, props.data]]
      : [];
  const showDropzone = multiple || !props.data;

  return (
    <div className="flex flex-col gap-2">
      {fileEntries.map(([fKey, file]) => (
        <FileListItem
          key={fKey}
          file={file}
          fileKey={fKey}
          onFileNameChange={onFileNameChange}
          onFileRemove={onFileRemove}
        />
      ))}
      {showDropzone && (
        <FileUploadController
          disabled={isUploading || disabled}
          multiple={multiple}
          uploadKey={fieldKey}
          onFileSelect={(file) => onFileSelect(file, fieldKey)}
        >
          <div
            className={twJoin(
              "w-full rounded-md border border-dashed",
              isError && "border-red-600 ring-2 ring-red-400/25",
            )}
          >
            <div className="flex gap-4 p-4">
              <div className="flex min-w-0 gap-1">
                {getDropzoneIcon()}
                <div className="flex min-w-0 flex-col">
                  <span className="font-inter-semibold-13px text-gray-800">
                    {multiple
                      ? "Drag files here or click to upload"
                      : "Drag a file here or click to upload"}
                  </span>
                  <span className="font-inter-normal-12px text-gray-500">
                    {getDropzoneDescription()}
                  </span>
                  <div className="mt-2">
                    <Button isDisabled={disabled} size="sm" variant="secondary">
                      {multiple ? "Upload file(s)" : "Upload a file"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FileUploadController>
      )}
    </div>
  );
};
